import { useCallback, useEffect, useState } from 'react';
import { faBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import {
  Notification,
  NotificationService,
  PagedResponseMeta,
} from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { NotificationCard } from './components/NotificationCard';

export const Notifications = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const pageParam = Number(searchParams.get('page') || 1) || 1;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [meta, setMeta] = useState<PagedResponseMeta | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'UrbanSitter - Notifications';
  }, []);

  const sitterId = pathId ? Number(pathId) : undefined;

  const fetchPage = useCallback(
    async (page: number, receiverId: number): Promise<void> => {
      setLoading(true);
      const config = {
        params: {
          page,
          receiverId,
          sort: 'created',
          'created.dir': 'desc',
        },
      };
      const resp = (await NotificationService.getByFilter(config)).data;
      setNotifications(resp.data);
      setMeta(resp.meta);
      setLoading(false);
      if (page !== 1) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [],
  );

  useEffect(() => {
    if (!user?.id || !sitterId) return;
    if (sitterId !== user.id) {
      navigate(`/sitter/${user.id}/notifications`, { replace: true });
      return;
    }
    fetchPage(pageParam, sitterId);
  }, [user?.id, sitterId, pageParam, fetchPage, navigate]);

  useEffect(() => {
    if (loading) return;
    if (!user?.id || sitterId !== user.id) return;
    if (stores.notifications.unreadNotificationsCount === 0) return;
    stores.notifications.markRead(user.id);
  }, [loading, user?.id, sitterId, stores.notifications]);

  const goToPage = (page: number): void => {
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.set('page', String(page));
      return next;
    });
  };

  if (loading) {
    return (
      <div className='container py-8'>
        <h1 className='text-2xl font-semibold mb-4'>{t('pages.notifications.header')}</h1>
        <p className='text-neutral-600'>{t('pages.notifications.loading')}</p>
      </div>
    );
  }

  const isEmpty = notifications.length === 0;

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-6'>{t('pages.notifications.header')}</h1>

      {isEmpty && (
        <div className='text-center max-w-md mx-auto py-10'>
          <FontAwesomeIcon icon={faBell} className='text-6xl text-neutral-300 mb-4' />
          <h4 className='text-lg font-medium mb-2'>{t('pages.notifications.emptyHeading')}</h4>
          <p className='text-sm text-neutral-700'>
            <a href='/app/job-board' id='express-interest' className='font-semibold underline'>
              {t('pages.notifications.emptyJobBoardLink')}
            </a>{' '}
            {t('pages.notifications.emptyBodyAfter')}
          </p>
        </div>
      )}

      {!isEmpty && (
        <div className='max-w-3xl'>
          {notifications.map((n) => (
            <NotificationCard key={n.id} notification={n} />
          ))}

          {meta && meta.pageCount !== undefined && meta.pageCount > 1 && (
            <div className='flex items-center justify-center gap-3 mt-6'>
              <Button
                buttonType={ButtonType.secondary}
                disabled={(meta.page ?? 1) <= 1}
                onClick={(): void => goToPage((meta.page ?? 1) - 1)}
              >
                {t('common.previous')}
              </Button>
              <span className='text-sm text-neutral-700'>
                {t('pages.notifications.pageOf', { current: meta.page, total: meta.pageCount })}
              </span>
              <Button
                buttonType={ButtonType.secondary}
                disabled={meta.lastPage === true || (meta.page ?? 1) >= (meta.pageCount ?? 1)}
                onClick={(): void => goToPage((meta.page ?? 1) + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
